import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Alert,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { supabase, signInWithOAuth } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { BRAND } from '../lib/brand';
import { bootstrapProfile, setPendingInviteToken } from '../lib/onboarding';


const Signup: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // A team invite link routes here as /signup?invite=TOKEN&email=... - the
  // token has to survive both the email-confirmation round trip and OAuth's
  // redirect, so it's stashed in localStorage rather than threaded through
  // as a query param the whole way (see lib/onboarding.ts).
  React.useEffect(() => {
    const invite = params.get('invite');
    if (invite) setPendingInviteToken(invite);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePassword = (password: string): { 
    length: boolean, 
    uppercase: boolean, 
    lowercase: boolean, 
    number: boolean 
  } => {
    return {
      length: password.length >= 14,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password)
    };
  };

  const passwordValidation = validatePassword(password);

  // OAuth sign-in IS sign-up for first-time users - account provisioning
  // happens in the OAuth callback via the provision_profile RPC.
  const handleOAuthSignup = async (provider: 'azure' | 'github') => {
    setError(null);
    setLoading(true);
    try {
      const { error: oauthError } = await signInWithOAuth(provider);
      if (oauthError) throw oauthError;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : `${provider} signup failed`);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate inputs
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    const passwordChecks = validatePassword(password);
    const passwordValid = Object.values(passwordChecks).every(check => check);

    if (!passwordValid) {
      setError('Password does not meet requirements');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!termsAccepted) {
      setError('You must accept the terms and conditions');
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            terms_accepted: termsAccepted,
          },
          emailRedirectTo: `${window.location.origin}/dashboard`
        }
      });

      if (error) throw error;

      // Account bootstrap goes through provision_profile/accept_team_invitation
      // (both SECURITY DEFINER) - direct organizations/profiles inserts from
      // the client are impossible by design: the org RLS policy needs a
      // profile to exist first, and with email confirmation on there isn't
      // even a session yet.
      if (data.session) {
        const { error: provisionError } = await bootstrapProfile();
        if (provisionError) throw provisionError;
        navigate('/dashboard');
      } else if (data.user) {
        // Email confirmation required: provisioning happens on first
        // authenticated load (AppLayout self-heals missing profiles).
        setSuccess('Please check your email for a confirmation link to complete your registration.');
      }
    } catch (err: unknown) {
      // A distinct error/message for "this email is already registered" lets
      // an attacker enumerate real accounts by trying candidate emails
      // against signup. Detect it (supabase-js AuthError.code, with a
      // message-text fallback since the code field is a newer addition and
      // some GoTrue error paths may not set it) and show the same
      // check-your-email message signup shows for a brand-new address.
      const authErr = err as { code?: string; message?: string };
      const isDuplicate =
        authErr?.code === 'user_already_exists' ||
        /already registered|already exists/i.test(authErr?.message ?? '');
      if (isDuplicate) {
        console.error('Signup failed (duplicate account):', err);
        setSuccess('Please check your email for a confirmation link to complete your registration.');
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Signup failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        bgcolor: 'background.default'
      }}
    >
      <Container maxWidth="xs">
        <Paper 
          elevation={6} 
          sx={{ 
            p: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center' 
          }}
        >
          <Typography component="h1" variant="h5">
            Sign Up for {BRAND.productName}
          </Typography>

          {error && (
            <Alert 
              severity="error" 
              sx={{ width: '100%', mt: 2 }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ width: '100%', mt: 2 }}
            >
              {success}
            </Alert>
          )}

          <Box 
            component="form" 
            onSubmit={handleSignup} 
            sx={{ width: '100%', mt: 3 }}
          >
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={email.length > 0 && !validateEmail(email)}
              helperText={
                email.length > 0 && !validateEmail(email) 
                  ? 'Invalid email format' 
                  : ''
              }
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <List dense>
              <ListItem>
                <ListItemIcon>
                  {passwordValidation.length ? <CheckIcon color="success" /> : <CloseIcon color="error" />}
                </ListItemIcon>
                <ListItemText primary="At least 14 characters" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  {passwordValidation.uppercase ? <CheckIcon color="success" /> : <CloseIcon color="error" />}
                </ListItemIcon>
                <ListItemText primary="Contains uppercase letter" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  {passwordValidation.lowercase ? <CheckIcon color="success" /> : <CloseIcon color="error" />}
                </ListItemIcon>
                <ListItemText primary="Contains lowercase letter" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  {passwordValidation.number ? <CheckIcon color="success" /> : <CloseIcon color="error" />}
                </ListItemIcon>
                <ListItemText primary="Contains number" />
              </ListItem>
            </List>

            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="confirm-password"
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirm-password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmPassword.length > 0 && password !== confirmPassword}
              helperText={
                confirmPassword.length > 0 && password !== confirmPassword 
                  ? 'Passwords do not match' 
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I accept the{' '}
                  <Link href="/terms" target="_blank" >
                    Terms and Conditions
                  </Link>
                </Typography>
              }
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Signing Up...' : 'Sign Up'}
            </Button>

            <Divider sx={{ my: 2 }}>Or sign up with</Divider>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                onClick={() => handleOAuthSignup('azure')}
                disabled={loading}
              >
                Azure
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={() => handleOAuthSignup('github')}
                disabled={loading}
              >
                GitHub
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Link
                  href="/login"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/login');
                  }}
                >
                  Log In
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Signup;
